import styled from 'styled-components'

import { theme } from 'styles/theme'
import { SmallExclamationMarkIcon, SmallTimesMarkIcon, StatusDotIcon, type ColorWithText } from '../Icon/Icon'

import type { ComponentPropsWithoutRef } from 'react'
import { CenterOpenState } from 'models/Navbar/CenterOpenState'
import { BodyText, BoldText } from '../Typography/Typography'
import { VehicleStatus } from 'models/Vehicles/VehicleStateModel'
import { DiscrepancyReportStatus } from 'models/DiscrepancyReports/DiscrepancyReportsModel'

const vehicleStatusToColorAndTitleMap: Record<VehicleStatus, ColorWithText> = {
  [VehicleStatus.Online]: { color: theme.colors.GreenPrimary, text: 'Online' },
  [VehicleStatus.Offline]: { color: theme.colors.MediumGrey, text: 'Offline' },
  [VehicleStatus.OutOfService]: { color: theme.colors.Red, text: 'Out of service' },
} as const

const VehicleStatusDotWithText = styled(StatusDotIcon)`
  display: flex;
  align-items: center;
  font-size: ${theme.fontSizes.small};
  gap: 0.4em;
`

interface VehicleStatusIndicatorProps extends ComponentPropsWithoutRef<typeof VehicleStatusDotWithText> {
  vehicleStatus: VehicleStatus | undefined
}

export function VehicleStatusIndicator({ vehicleStatus, ...props }: VehicleStatusIndicatorProps) {
  const { color, text } = 
    vehicleStatus !== undefined ?
      vehicleStatusToColorAndTitleMap[vehicleStatus] : 
      { color: theme.colors.MediumGrey, text: 'Status unknown'}
  return (
    <VehicleStatusDotWithText {...{ ...props, color, text }} />
  )
}

export type VehicleStatusIndicatorType = typeof VehicleStatusIndicator

const DiscrepancyReportStatusDotWithText = styled(StatusDotIcon)`
  align-items: center;
  font-size: ${theme.fontSizes.small};
  gap: 0.4em;
`

interface DiscrepancyReportStatusIndicatorProps extends ComponentPropsWithoutRef<typeof DiscrepancyReportStatusDotWithText> {
  discrepancyReportStatus: DiscrepancyReportStatus
}

const discrepancyReportToColorAndTitleMap: Record<DiscrepancyReportStatus, ColorWithText> = {
  [DiscrepancyReportStatus.Resolved]: { color: theme.colors.GreenPrimary, text: 'Resolved' },
  [DiscrepancyReportStatus.Unresolved]: { color: theme.colors.Red, text: 'Unresolved' },
} as const


export function DiscrepancyReportStatusIndicator({ discrepancyReportStatus, ...props }: DiscrepancyReportStatusIndicatorProps) {
  const { color, text } = 
    discrepancyReportStatus !== undefined ?
    discrepancyReportToColorAndTitleMap[discrepancyReportStatus] : 
      { color: theme.colors.MediumGrey, text: 'Status unknown'}
  return <DiscrepancyReportStatusDotWithText {...{ ...props, color, text }} />
}

export type DiscrepancyReportStatusIndicatorType = typeof DiscrepancyReportStatusIndicator

const centerStatusToColorAndTitleMap: Record<CenterOpenState, ColorWithText> = {
  [CenterOpenState.OPEN]: { color: theme.colors.GreenPrimary, text: 'Center Open' },
  [CenterOpenState.OPENING_CHECKLIST]: { color: theme.colors.Yellow, text: 'Center Opening' },
  [CenterOpenState.CLOSING_CHECKLIST]: { color: theme.colors.Yellow, text: 'Center Closing' },
  [CenterOpenState.CLOSING]: { color: theme.colors.Yellow, text: 'Center Closing' },
  [CenterOpenState.CAN_BE_CLOSED]: { color: theme.colors.Yellow, text: 'Center Closing' },
  [CenterOpenState.CLOSED]: { color: theme.colors.RedBright, text: 'Center Closed' },
} as const

export interface CenterStatusIndicatorProps {
  centerStatus: CenterOpenState | null
  centerName: string | undefined
  isSidebarOpen: boolean
}

const CenterStatusContainer = styled.div`
  display: flex;
  flex-direction: column;

  gap: 0.5em;
`

export function getShortCenterName(centerName: string | undefined): string {
  if (!centerName){
    return 'NO CENTER NAME'
  }
  return centerName.split(' ').map(namePart => namePart[0]).join('').substring(0, 2)
}



export function CenterStatusIndicator({ centerStatus, centerName, isSidebarOpen, ...props }: CenterStatusIndicatorProps) {
  const { color, text } = centerStatus !== null ? 
    centerStatusToColorAndTitleMap[centerStatus] : 
    { color: theme.colors.MediumGrey, text: 'Loading...' } 
  return <CenterStatusContainer>
          { isSidebarOpen && <BoldText>{centerName ?? 'Loading....'}</BoldText>}
      <StatusDotIcon {...{ color, text, ...props }}>
        { 
          isSidebarOpen ? 
          <BodyText>{ text }</BodyText> : 
          <BoldText>{ getShortCenterName(centerName ?? '') }</BoldText>
        }
      </StatusDotIcon>
    </CenterStatusContainer>
}

export type CenterStatusIndicatorType = typeof CenterStatusIndicator

export enum RecordStatus {
  None = 'none',
  Warning = 'warning',
  Danger = 'danger',
}

interface RecordStatusIndicatorProps extends ComponentPropsWithoutRef<'span'> {
  recordStatus: RecordStatus
}

const RecordStatusContainer = styled.span`
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  text-align: start;
  gap: 1ch;
  svg {
    font-size: 1.2em;
    margin: 0 auto;
  }
`

export function RecordStatusIndicator({
  recordStatus = RecordStatus.None,
  children,
  ...props
}: RecordStatusIndicatorProps) {
  return (
    <RecordStatusContainer {...props}>
      {recordStatus === RecordStatus.Warning ? (
        <SmallExclamationMarkIcon {...({ style: { '--color': theme.colors.warning } } as WithStyle)} />
      ) : null}
      {recordStatus === RecordStatus.Danger ? (
        <SmallTimesMarkIcon {...({ style: { '--color': theme.colors.danger } } as WithStyle)} />
      ) : null}
      {children}
    </RecordStatusContainer>
  )
}

export type RecordStatusIndicatorType = typeof RecordStatusIndicator
